import React, { useState } from 'react';
import {
  Group,
  Title,
  Text,
  Button,
  Badge,
  ActionIcon,
  Menu,
  Stack,
  Switch,
  Box
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconCalendar,
  IconCheck,
  IconDotsVertical,
  IconChevronLeft,
  IconCalendarCog,
  IconMessageCircle,
  IconTrash,
  IconLock,
  IconLockOpen,
  IconShare,
  IconRefresh,
  IconCalendarEvent,
  IconCalendarOff,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { MealPlan } from '../../types/mealPlan';
import { formatDateRange, isMealPlanExpired, isMealPlanPast } from '../../utils/mealPlanUtils';
import { afterPaint } from '../../utils/afterPaint';

interface MealPlanHeaderProps {
  mealPlan: MealPlan;
  userId: string;
  onBack?: () => void;
  onDelete?: (mealPlan: MealPlan) => void;
  onActivatePlan: () => void;
  onRegenerateGroceryList?: () => void;
  onOpenPreferences?: () => void;
  onMealPlanFeedback?: () => void;
  onToggleShare?: (isShared: boolean) => void;
  onShareLink?: () => void;
  onSyncCalendar?: () => void;
  onRemoveCalendar?: () => void;
  loadingActivation: boolean;
  loadingRegeneration?: boolean;
  loadingCalendarSync?: boolean;
  hasActiveSubstitutions: boolean;
  // True when every day has been removed; an empty plan can't be activated
  hasNoMeals?: boolean;
  getStatusColor: (status: string) => string;
  isOwner: boolean;
  profilePublic?: boolean;
  calendarConnected?: boolean;
  calendarSyncedAt?: string;
}

export const MealPlanHeader: React.FC<MealPlanHeaderProps> = ({
  mealPlan,
  userId,
  onBack,
  onDelete,
  onActivatePlan,
  onRegenerateGroceryList,
  onOpenPreferences,
  onMealPlanFeedback,
  onToggleShare,
  onShareLink,
  onSyncCalendar,
  onRemoveCalendar,
  loadingActivation,
  loadingRegeneration = false,
  loadingCalendarSync = false,
  hasActiveSubstitutions,
  hasNoMeals = false,
  getStatusColor,
  isOwner,
  profilePublic = false,
  calendarConnected = false,
  calendarSyncedAt,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const isPast = isMealPlanPast(mealPlan);
  const isExpired = isMealPlanExpired(mealPlan);
  const [menuOpened, setMenuOpened] = useState(false);
  // True while a menu-item navigation is closing the context menu: the close
  // must skip its exit transition and paint before the route changes, or
  // Chrome's predictive-back screenshot keeps the open menu (see afterPaint).
  const [instantMenuClose, setInstantMenuClose] = useState(false);

  const handleDeleteMealPlan = () => {
    if (onDelete) {
      onDelete(mealPlan);
    }
  };

  const handleEnableSharing = () => {
    setInstantMenuClose(true);
    setMenuOpened(false);
    afterPaint(() => {
      navigate('/profile?section=privacy');
      setInstantMenuClose(false);
    });
  };

  return (
    <Group>
      {onBack && (
        <ActionIcon 
          variant="subtle" 
          onClick={onBack}
          size="lg"
        >
          <IconChevronLeft size={20} />
        </ActionIcon>
      )}
      <Stack gap="xs" style={{ flex: 1 }}>
        <Box data-tour="meal-plan-intro" style={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}>
          <Stack gap="xs" className="page-header">
            {/* Wrapping + flexShrink 0: at 320px a nowrap row shrink-ellipsizes
                the badge to "ACTI…"; wrapping drops it under the title intact */}
            <Group gap="sm">
              <Title order={2} style={{ whiteSpace: 'nowrap' }}>Meal plan</Title>
              <Badge
                color={
                  isExpired
                    ? 'gray'
                    : isPast && mealPlan.status === 'active'
                      ? getStatusColor('completed')
                      : getStatusColor(mealPlan.status)
                }
                style={{ flexShrink: 0 }}
              >
                {isExpired
                  ? 'expired'
                  : isPast && mealPlan.status === 'active'
                    ? 'completed'
                    : mealPlan.status}
              </Badge>
            </Group>
            <Group gap="sm" wrap="nowrap">
              <IconCalendar size={16} style={{ flexShrink: 0 }} />
              <Text c="dimmed" className="page-subtitle">{formatDateRange(mealPlan, true)}</Text>
            </Group>
          </Stack>
        </Box>
      </Stack>
      {isOwner && mealPlan.status === 'draft' && !isExpired && (
        <Button
          leftSection={<IconCheck size={16} />}
          onClick={onActivatePlan}
          loading={loadingActivation}
          disabled={hasActiveSubstitutions || hasNoMeals}
          visibleFrom="sm"
          color="indigo"
          data-tour="activate-button-desktop"
        >
          {loadingActivation
            ? 'Activating plan...'
              : 'Activate plan'
          }
        </Button>
      )}
      {isOwner && mealPlan.status === 'active' && !isPast && mealPlan.lastModifiedAt && mealPlan.groceryListGeneratedAt && new Date(mealPlan.lastModifiedAt) > new Date(mealPlan.groceryListGeneratedAt) && onRegenerateGroceryList && (
        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={onRegenerateGroceryList}
          loading={loadingRegeneration || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'}
          disabled={hasActiveSubstitutions || loadingRegeneration || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'}
          visibleFrom="sm"
          variant="light"
          color="blue"
        >
          {loadingRegeneration || mealPlan.groceryListStatus?.status === 'pending' || mealPlan.groceryListStatus?.status === 'generating'
            ? 'Refreshing...'
              : 'Refresh shopping list'
          }
        </Button>
      )}

      {/* Context Menu */}
      {(onOpenPreferences || onToggleShare || onShareLink || onMealPlanFeedback || onDelete) && (
        <Menu position="bottom-end" opened={menuOpened} onChange={setMenuOpened} transitionProps={instantMenuClose ? { duration: 0 } : undefined}>
          <Menu.Target>
            <ActionIcon variant="subtle" size="lg">
              <IconDotsVertical size={20} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
          {onOpenPreferences && (
            <Menu.Item
              leftSection={<IconCalendarCog size={isMobile ? 18 : 14} stroke={1.5} />}
              onClick={onOpenPreferences}
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              Meal plan preferences
            </Menu.Item>
          )}

          {onToggleShare && onShareLink && (
            <Menu.Item
              closeMenuOnClick={!profilePublic}
              onClick={(e) => {
                if (!profilePublic) {
                  // Profile not public - navigate to settings
                  handleEnableSharing();
                } else {
                  // Only toggle if not clicking on the switch or copy button
                  const target = e.target as HTMLElement;
                  if (!target.closest('[data-switch-wrapper]') && !target.closest('[data-copy-button]')) {
                    onToggleShare(!mealPlan.isShared);
                  }
                }
              }}
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              <Group justify="space-between" wrap="nowrap" gap="md" w="100%">
                <Group gap="xs" style={{ flex: 1 }}>
                  {profilePublic ? (
                    mealPlan.isShared ? (
                      <IconLockOpen size={isMobile ? 18 : 14} stroke={1.5} />
                    ) : (
                      <IconLock size={isMobile ? 18 : 14} stroke={1.5} />
                    )
                  ) : (
                    <IconShare size={isMobile ? 18 : 14} stroke={1.5} />
                  )}
                  <Text size={isMobile ? "md" : "sm"}>{profilePublic ? 'Sharing' : 'Enable sharing'}</Text>
                  {profilePublic && (
                    <Box data-switch-wrapper>
                      <Switch
                        checked={!!mealPlan.isShared}
                        onChange={(event) => onToggleShare(event.currentTarget.checked)}
                      />
                    </Box>
                  )}
                </Group>
                {profilePublic && (
                  <ActionIcon
                    component="div"
                    variant="subtle"
                    size="sm"
                    disabled={!mealPlan.isShared}
                    data-copy-button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mealPlan.isShared) onShareLink();
                    }}
                    style={{ cursor: mealPlan.isShared ? 'pointer' : 'default' }}
                  >
                    <IconShare size={16} />
                  </ActionIcon>
                )}
              </Group>
            </Menu.Item>
          )}

          {isOwner && calendarConnected && mealPlan.status === 'active' && onSyncCalendar && (
            <Menu.Item
              leftSection={<IconCalendarEvent size={isMobile ? 18 : 14} stroke={1.5} />}
              onClick={onSyncCalendar}
              disabled={loadingCalendarSync}
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              {loadingCalendarSync ? 'Syncing...' : calendarSyncedAt ? 'Re-sync to calendar' : 'Sync to calendar'}
            </Menu.Item>
          )}

          {isOwner && calendarConnected && mealPlan.status === 'active' && calendarSyncedAt && onRemoveCalendar && (
            <Menu.Item
              leftSection={<IconCalendarOff size={isMobile ? 18 : 14} stroke={1.5} />}
              onClick={onRemoveCalendar}
              disabled={loadingCalendarSync}
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              Remove from calendar
            </Menu.Item>
          )}

          {(onOpenPreferences || onToggleShare || onMealPlanFeedback || onDelete) && <Menu.Divider />}

          {onMealPlanFeedback && (
            <Menu.Item
              leftSection={<IconMessageCircle size={isMobile ? 18 : 14} stroke={1.5} />}
              onClick={onMealPlanFeedback}
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              Send feedback
            </Menu.Item>
          )}

          {onDelete && (
            <Menu.Item
              leftSection={<IconTrash size={isMobile ? 18 : 14} stroke={1.5} />}
              color="red"
              onClick={handleDeleteMealPlan}
              disabled={
                hasActiveSubstitutions ||
                mealPlan.groceryListStatus?.status === 'pending' ||
                mealPlan.groceryListStatus?.status === 'generating'
              }
              style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
            >
              Delete meal plan
            </Menu.Item>
          )}
        </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
};
